<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage, Checkbox, Tooltip, InfoTooltip } from '@/shared/ui';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import type { Position } from '@/entities/Position';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import CopyIcon from '@/shared/assets/icons/CopyIcon.svg';
  import { formatMemberAuditDateTime } from '../lib/member-audit-datetime';

  const MAX_NAME_DISPLAY_LENGTH = 100;

  const props = defineProps<{
    position: Position;
    isSelected: boolean;
  }>();

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    edit: [id: number];
    info: [id: number];
    copy: [id: number];
    delete: [id: number];
  }>();

  const togglePosition = (id: number) => {
    emit('toggle-selection', id);
  };

  const handleEdit = () => {
    emit('edit', props.position.id);
  };
  const handleInfo = () => {
    emit('info', props.position.id);
  };

  const handleDelete = () => {
    emit('delete', props.position.id);
  };

  const handleCopy = () => {
    emit('copy', props.position.id);
  };

  const actions = [
    { icon: PencilIcon, tooltip: 'Editar', handler: handleEdit },
    { icon: CopyIcon, tooltip: 'Clonar', handler: handleCopy },
    { icon: Trash, tooltip: 'Deletar', handler: handleDelete },
  ];

  const displayName = computed(() => {
    const name = props.position.name;
    if (name.length <= MAX_NAME_DISPLAY_LENGTH) return name;
    return `${name.slice(0, MAX_NAME_DISPLAY_LENGTH)}...`;
  });
</script>

<template>
  <tr class="table-row-hover transition-colors duration-300 ease-out">
    <td class="w-20 align-middle text-center">
      <Checkbox
        :id="position.id"
        :checked="isSelected"
        @toggle="togglePosition(position.id)"
      />
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left w-70 min-w-0 overflow-hidden">
      <Tooltip
        :content="position.name"
        placement="top"
      >
        <span class="block truncate">{{ displayName }}</span>
      </Tooltip>
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ position.department?.code || '-' }}
    </td>
    <td class="pl-6 py-4 text-sm text-gray-07 w-50">
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <Tooltip
          v-for="action in actions"
          :key="action.tooltip"
          :content="action.tooltip"
          placement="top"
          class="flex items-center"
        >
          <button @click="action.handler">
            <IconImage
              :src="action.icon"
              :alt="action.tooltip"
            />
          </button>
        </Tooltip>
        <InfoTooltip
          :icon="InfoIcon"
          title="Informações"
          :created-at="formatMemberAuditDateTime(position.createdAt)"
          :created-by="position.creator?.name"
          :updated-at="formatMemberAuditDateTime(position.updatedAt)"
          :updated-by="position.updater?.name"
          placement="top"
        >
          <button @click="handleInfo">
            <IconImage
              :src="InfoIcon"
              alt="Informações"
            />
          </button>
        </InfoTooltip>
      </div>
    </td>
  </tr>
</template>
